import { jsx } from "panel";
import { ButtonEl } from "common/components/button";
import { ButtonTheme, ButtonType } from "common/components/button/constants";
import { EntryPart } from "common/query-builder/query-entry-section/types";
import { Placement } from "common/components/tooltip/types";
import { ResourceType, SPECIAL_EVENTS } from "common/report/constants";
import { isFirstTimeEventFilter } from "common/widgets/property-filter-menu/models/utils";
import { hasComputedListFilters, isListOfObjectsFilter } from "common/widgets/property-filter-menu/utils";
import { isMultiAttributionFilter } from "common/query-builder/query-builder/util";
import { BaseEntryRowEl } from "../base/template";
import { BehavioralSubFilters, BehavioralSubItem, PropertySelector } from "../behavior/template";
import { isEventBasedBehavioralSegmentation } from "../behavior/utils";
import { FilterParts } from "./parts";
export function FilterEntryRowEl({ section, entry, index }) {
  const hasProperty = this.filter.hasSelectedProperty();
  const hasNewUI = section.hasNewQBUI();
  const isFirstTimeFilter = isFirstTimeEventFilter(this.filter);
  const isClearAction = entry.quickDelete && entry.clearable && section.state.stagedRows.length === 2 && !section.state.stagedRow;
  const hasConjunction = hasNewUI && section.state.suffixRenderer && section.attr(`entries`).length > 1 && index === 0;
  const isFirstListItemFilter = !!this.section.params.listProperty && this.index === 0;
  const handlePropertySelect = (selection, queryReason, trackingProps) => this.handlePropertyItemSelected(selection, queryReason, trackingProps);
  const isProfileHistoryEnabled = section.isProfileHistoryEnabled();
  const selectorRenderer = ({ rowActions }) => {
    var _a, _b;
    let eventNames = section.attr(`event-names`);
    if (isMultiAttributionFilter(this.filter)) {
      eventNames = [...eventNames, SPECIAL_EVENTS.ALL_EVENTS];
    }
    const filterParts = /* @__PURE__ */ jsx(FilterParts, {
      filter: this.filter,
      hideListQuantifier: entry.hideListQuantifier,
      hasNewUI: section.hasNewQBUI(),
      isValueActive: section.state.openEntry.index === index && section.state.openEntry.part === EntryPart.FilterValue,
      setActiveEntry: (state) => section.helpers.setActiveEntry(state, { index, part: EntryPart.FilterValue }),
      updateEntry: (filter) => this.updateEntry(filter),
      onValueSelectMenuChange: (ev) => section.helpers.onSelectMenuChange(ev, index, EntryPart.FilterValue),
      onChangeOperator: () => this.onChangeOperator(),
      operatorOverrides: entry.operatorOverrides,
      hasPlaceholder: ((_a = section.state.placeholders.get(index)) == null ? void 0 : _a[0]) === EntryPart.FilterValue,
      eventNames,
      projectId: section.attr(`project-id`),
      workspaceId: section.attr(`workspace-id`),
      userId: section.attr(`user-id`),
      dataGroupId: section.attr(`data-group-id`),
      readonly: section.attr(`readonly`),
      withQuickDelete: entry.filterOnly && entry.quickDelete,
      withQuickRename: entry.filterOnly && entry.quickRename,
      showTypecastControl: !entry.filterOnly && this.filter.resourceType !== ResourceType.Cohort,
      disabledTypecastTypes: section.params.disabledTypecastTypes,
      rowActions,
      unit: this.getUnit(),
      isProfileHistoryEnabled,
      hideEvents: section.attr(`hide-events`)
    });
    return entry.filterOnly ? /* @__PURE__ */ jsx(null, null, entry.quickDelete && /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(ButtonEl, {
      sel: ".mp-query-entry-delete-row",
      class: { "is-clear-action": isClearAction },
      attrs: {
        elref: `filterDeleteButton`,
        theme: ButtonTheme.Tertiary,
        icon: `x`,
        type: ButtonType.Destructive,
        tooltip: {
          "delay-in": 10,
          "delay-out": 10,
          placement: Placement.Bottom,
          text: `Remove Value(s)`
        }
      },
      on: { click: () => this.handleFilterDeleteClick() }
    })), entry.quickRename && /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(ButtonEl, {
      sel: ".mp-query-entry-rename-row",
      attrs: {
        elref: `filterRenameButton`,
        theme: ButtonTheme.Tertiary,
        icon: `edit`,
        type: ButtonType.Base,
        tooltip: {
          "delay-in": 10,
          "delay-out": 10,
          placement: Placement.Bottom,
          text: `Rename Value(s)`
        }
      },
      on: { click: () => this.handleRenameClick() }
    })), filterParts) : /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(PropertySelector, {
      entry: this.entry,
      handleSelect: handlePropertySelect,
      index: this.index,
      isPlaceholder: this.isPlaceholder(),
      section: this.section,
      selected: this.entry.event,
      isForFilter: true
    }), isEventBasedBehavioralSegmentation(this.entry.event) && ((_b = this.entry.event) == null ? void 0 : _b[`behavior`]) && /* @__PURE__ */ jsx(BehavioralSubItem, {
      entry: this.entry,
      selected: this.entry.event,
      handleSelect: handlePropertySelect,
      index: this.index,
      isPlaceholder: this.isPlaceholder(),
      section: this.section
    }), hasProperty ? filterParts : null);
  };
  const suffixRenderer = () => /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(BehavioralSubFilters, {
    entry: this.entry,
    handleSelect: (selection, queryReason, trackingProps) => this.handlePropertyItemSelected(selection, queryReason, trackingProps),
    index: this.index,
    section: this.section,
    selected: this.entry.event
  }));
  const actionsRenderer = () => /* @__PURE__ */ jsx(null, null, isListOfObjectsFilter(this.filter) && hasComputedListFilters(this.filter) === false && /* @__PURE__ */ jsx(ButtonEl, {
    attrs: {
      elref: `addItemFilterButton`,
      theme: ButtonTheme.Tertiary,
      icon: `plus`,
      role: `button`,
      tooltip: {
        text: `Add`,
        placement: Placement.Top
      }
    },
    on: {
      click: () => this.section.helpers.showPlaceholderForEntry({ part: EntryPart.FilterValue, index: this.index })
    }
  }));
  return /* @__PURE__ */ jsx(BaseEntryRowEl, {
    index,
    el: section,
    entry,
    hideActions: entry.filterOnly || !section.hasNewQBUI() && (isListOfObjectsFilter(this.filter) || isFirstListItemFilter),
    extraEntryClass: {
      "is-filter-only": entry.filterOnly,
      "is-first-time-filter": this.filter && isFirstTimeFilter,
      "has-conjunction": hasConjunction
    },
    rowRenderer: {
      selectorRenderer,
      suffixRenderer,
      actionsRenderer
    },
    isPlaceholder: this.isPlaceholder(),
    rowIcon: this.selectorIcon()
  });
}
